import { AfterViewInit, Component, HostListener, Injector, OnDestroy, OnInit, ViewContainerRef, ViewEncapsulation, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, forkJoin, Observable, Subject } from 'rxjs';
import { StartupService } from '../../projects/startup/src/lib/startup.service';
import { distinctUntilChanged, filter, map, take, takeUntil } from 'rxjs/operators';
import { ProjectModel } from '@features/projects/models/projects.models';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { ParticleService } from '@core/services/particle.service';
import { getCurrentProject, ProjectState } from '@features/projects/store/reducers/projects.reducer';
import { ContactState } from '@features/contacts/store/contact.reducer';
import { CalendarReminderService } from '@features/orbitax-meet/calendar-reminder/calendar-reminder.service';
import { SocketClusterService } from '@core/services/socket-cluster.service';
import { AppNavigationService } from '@core/services/app.navigation.service';
import { CurrentUserComponent } from '@shared/components/current-user/current-user.component';
import { CurrentProjectComponent } from '@features/projects/components/current-project/current-project.component';
import { Configuration } from '@shared/models/configuration';
import { GetFeaturesAction, GetUserInfoAction, RefreshAccessTokenAction } from '@shared/store/actions/auth.actions';
import { getAccessToken, getClaims, getFeaturesInfo, getTempAccessToken, getUserInfo, isAuthenticated } from '@shared/store/selectors/security.selector';
import { GetCurrentContactAction } from '@features/contacts/store/contact.action';
import { AuthorizationType } from '@shared/models/AuthorizationType';
import { InvokeLoadProjectByProjectIdAction } from '@features/projects/store/actions/projects.actions';
import { GetBasketAction } from '@shared/store/actions/particle.actions';
import { FuseTranslationLoaderService } from '@barebone-fuse-services/translation-loader.service';
import { ItpDesktopService } from '@core/services/itpdesktopservices/itp-desktop.service';
import { AuthApiService } from '@core/services/auth-api.service';
import { ItpDesktopEventDispatchService } from '@core/services/itpdesktopservices/Itp-desktop-event-dispatcher.service';
import { NavigationEnd, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from "@angular/material/snack-bar";
import { DataContextFeedService } from "@shared/services/data-context-feed/data-context-feed.service";
import { FuseSplashScreenService } from "@barebone-fuse-services/splash-screen.service";
import { IQueryState } from '@shared/store/reducers/query.reducer';
import { getCurrentPlatformLink } from '@shared/store/reducers/particle.reduer';
import { LayoutType, PlatformToolLayoutService } from '@core/services/platform-tool-layout.service';
import { ProjectDemoTest } from "./copilot/demo-tests/project-demo-test";
import { WebSocketAutomationService } from "./copilot";
import { PresenceTrackingService } from '@core/services/presence-tracking.service';
import { MissionWorkTabService } from './copilot/services/mission-work-tab.service';
import { UserflowGate } from '@shared/services/userflow-gate.service';
import { SqaRunRoutingService } from '@core/services/sqa-run-routing.service';
import { SqaRunSignInService } from '@core/services/sqa-run-sign-in.service';
import {logger} from "@core/models/logger";
import { ViewportScaleService } from '@core/services/viewport-scale/viewport-scale.service';
import { SessionTimeoutService } from '@core/services/session-timeout/session-timeout.service';
import { DemoProvisioningService } from '@shared/services/demo-provisioning.service';
@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  // GEN-E-99-33 — started here so module-wise viewport scaling runs in either layout (v2.0 and Classic).
  private readonly viewportScaleService = inject(ViewportScaleService);
  title = 'orbitax-webshell';
  features: any;
  isAuthenticated$: Observable<number>;
  currentProject$: BehaviorSubject<ProjectModel>;
  private currentAccessToken: any;
  private Feature$: Observable<any>;
  private hasDemoAccessToken: boolean;
  private ngUnsubscribe = new Subject<void>();
  // ITP2-14141 — the session timeout is shared by every tab of the session; the service owns the countdown.
  private readonly sessionTimeout = inject(SessionTimeoutService);
  horizontalPosition: MatSnackBarHorizontalPosition = 'start';
  verticalPosition: MatSnackBarVerticalPosition = 'bottom';
  showCookiePolicyPopup: boolean = false;
  cookiePolicyUrl: any;

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event) {
    this.dataContextFeedService.disconnectUser(Configuration.socketConnectionId).subscribe(response => { });
  }
  constructor(private translate: TranslateService,
    private fuseTranslationLoader: FuseTranslationLoaderService,
    private cookieService: CookieService,
    private securityStore: Store<SecurityState>,
    private particleService: ParticleService,
    private projectStore: Store<ProjectState>,
    private router: Router,
    private contactStore: Store<ContactState>,
    private socketClusterService: SocketClusterService,
    private startupService: StartupService,
    private appNavigationService: AppNavigationService,
    private itpDesktopService: ItpDesktopService,
    private itpDesktopEventDispatchService: ItpDesktopEventDispatchService,
    private dataContextFeedService: DataContextFeedService,
    private fuseSplashScreenService: FuseSplashScreenService,
    public authService: AuthApiService,
    private queryStore: Store<IQueryState>,
    public platformToolLayoutService: PlatformToolLayoutService,
    private webSocketAutomationService: WebSocketAutomationService,
    private presenceTrackingService: PresenceTrackingService,
    private injector: Injector,
    public vcRef: ViewContainerRef,
    private userflowGate: UserflowGate,
    private sqaRunRouting: SqaRunRoutingService,
    private sqaRunSignIn: SqaRunSignInService,
    private missionWorkTab: MissionWorkTabService,
  ) {
    // MISN-E-210-01: a tab opened at ?missionWorkTab=<id> is that mission's Orbitax window - read before any redirect drops it.
    this.missionWorkTab.capture();
    this.currentAccessToken = null;
    this.hasDemoAccessToken = false;
    this.currentProject$ = new BehaviorSubject(null);
    this.particleService.setCurrentUserComponent(CurrentUserComponent);
    this.particleService.setCurrentProjectComponent(CurrentProjectComponent);
    // Add languages
    this.translate.addLangs(['en', 'tr']);
    // Set the default language
    this.translate.setDefaultLang('en');
    // Use a language

    this.fuseTranslationLoader.loadTranslations(...this.startupService.getLanguagePacks());

    // Use a language
    this.translate.use('en');
    this.socketClusterService.initializeScService();
    this.cookiePolicyUrl = Configuration.getConfig("CookiePolicyUrl");
    this.processLogger();
  }


  test401() {
    this.authService.getDataError401()
      .subscribe((response: any) => {
        console.log(`Worked with status = ${response.status}`);
      },
        error => console.log(`Failed with status = ${error.status}`));
  }

  test401Multiple() {
    let dataObservable$ = this.authService.getDataError401();
    let lookupObservable$ = this.authService.getLookupError401();

    forkJoin(dataObservable$, lookupObservable$)
      .subscribe(([dataResult, lookupResult]) => {
        console.log(`Worked with getData status = ${dataResult.status} and getLookup status = ${lookupResult.status}`);
      },
        error => console.log(`Failed with status = ${error.status}`));
  }
  ngAfterViewInit() {
    // setInterval(()=>{
    //   this.test401();
    //   this.test401Multiple();
    // },5000);

    this.sessionTimeout.start();
    // let sessionTimeOutTime = Configuration.getConfig('PlatformSessionTimeoutInMinutes') * 60;
    // sessionTimeOutTime = 1000 * (sessionTimeOutTime - 120);
    // interval(sessionTimeOutTime).pipe(map((x) => {
    //   if (this.currentAccessToken && !this.hasDemoAccessToken) {
    //     this.securityStore.dispatch(new RefreshAccessTokenAction(this.currentAccessToken.refresh_token));
    //   }
    // }));
  }

  ngOnInit(): void {
    const layout$ = toObservable(this.platformToolLayoutService.currentLayout, { injector: this.injector });
    combineLatest([layout$, this.securityStore.select(getUserInfo), this.securityStore.select(getClaims)]).pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([layout, userInfo, claims]) => {
      if (layout === LayoutType.PLATFORM_TOOL_APP_LAYOUT) {
        this.userflowGate.init(userInfo, claims);
      } else {
        this.userflowGate.reset();
      }
    });

    this.queryStore.pipe(select(getCurrentPlatformLink), takeUntil(this.ngUnsubscribe)).subscribe(state => {
      if (state) {
        this.resetIdleTimer()
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        (window as any).orbitaxPlatformGtag('set', { 'path_url': event.urlAfterRedirects });
      }
    });

    const self = this;
    this.particleService.setCurrentUserComponent(CurrentUserComponent);
    this.particleService.setCurrentProjectComponent(CurrentProjectComponent);
    this.isAuthenticated$ = this.securityStore.select(isAuthenticated);
    this.securityStore.select(getAccessToken).subscribe(accessToken => {
      if (accessToken) {
        this.authService.refreshOrbitaxWebsite();
        this.currentAccessToken = accessToken;
        this.securityStore.dispatch(new GetUserInfoAction(accessToken.access_token));
        this.securityStore.dispatch(new GetFeaturesAction());
        this.securityStore.dispatch(new GetBasketAction());
        // const startupApiKeys = Configuration.getConfig('StartupAPIKeys');
        // startupApiKeys.forEach(o => {
        //   const apiKey = Configuration.getConfig(o.APIKey);
        //   this.contactStore.dispatch(new StartupAction(o.Name, apiKey));
        // });
        this.contactStore.dispatch(new GetCurrentContactAction());
        this.startupService.onStartupAction();

        if (Configuration.getConfig('PresenceTrackerApi')) {
          // Presence tracking is deliberately decoupled from the main init chain:
          //   - deferred via setTimeout so a synchronous throw in presence code
          //     cannot disrupt the AppComponent ngOnInit flow
          //   - wrapped in try/catch as belt-and-braces for the same reason
          //   - the service itself has a kill switch, circuit breaker, and
          //     internal error handling (see presence-tracking.service.ts)
          setTimeout(() => {
            try {
              this.presenceTrackingService.startTracking();
            } catch (err) {
              console.warn('[presence] failed to start tracking, feature disabled', err);
            }
          }, 0);
        }

        // MISN-E-210-01: once the user is known, an Orbitax window registers and beats every 30 s (nothing for any other tab).
        setTimeout(() => {
          try {
            this.missionWorkTab.start();
          } catch (err) {
            console.warn('[mission] the Orbitax window did not register', err);
          }
        }, 0);

        // The Calls feature's page-load asks are CallsShellComponent's (ITP2-14993: never for a magic-link session).
        // ITP-E-337-05: a meeting's reminder shows as a toast ten minutes before it starts.
        this.securityStore.pipe(select(getUserInfo), filter(userInfo => !!userInfo?.email), take(1), takeUntil(this.ngUnsubscribe))
          .subscribe(() => this.injector.get(CalendarReminderService).start());
      }
    });

    this.securityStore.select(getTempAccessToken).subscribe(demoAccessToken => {
      if (demoAccessToken) {
        this.hasDemoAccessToken = true;
      } else {
        this.hasDemoAccessToken = false;
      }
    });
    this.Feature$ = this.securityStore.select(getFeaturesInfo);
    this.Feature$.subscribe(features => {
      self.features = features;
    });

    this.isAuthenticated$.subscribe(isAuth => {
      switch (isAuth) {
        case AuthorizationType.AUTHORIZED:
          if (window.location.href.search(/code=/i) !== -1) {
            window.history.pushState('', '', this.removeParam('code', window.location.href));
            //because we need to remove this code=021a6680d26b46efb603a58df58afba75df49fc191ce4329882c846027129a0f&state=FmMWTVYxzNadzaUmCwl8Zg
          }
          console.log('+++Remove code param in url++++');
          this.appNavigationService.resolveAppToAppRoute(this.projectStore, new InvokeLoadProjectByProjectIdAction(''));
          // TSP-E-157-22: an SQA session goes to the Xact Playground, wherever the company opens by default.
          this.sqaRunRouting.start();
          // TSP-E-157-26: ITP, not the IdP, records on the run who it signed in as.
          this.sqaRunSignIn.start();
          break;
        case AuthorizationType.UNAUTHORIZED:
          window.location.href = Configuration.DashboardUrl;
          break;
      }
    });

    this.projectStore.select(getCurrentProject).subscribe(project => {
      self.currentProject$.next(project);
    });
    this.showCookiePolicy();
    this.uncoverFuseSpashContents();
    //
    const sessionId = Configuration.getConfig('sessionId');
    this.socketClusterService.subscribeToChannel(sessionId).pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(response => {
      console.log('[SessionInfoSocketResponse]...')
      console.log(response);
      if (response.EventType === 'WaitScreenMessage') {
        // A demo start shows EAT's wait card rather than the splash (SHOW-E-44-01); its message follows the server.
        if (this.injector.get(DemoProvisioningService).updateWaitMessage(response.Payload?.Message)) {
          return;
        }
        if (this.fuseSplashScreenService.isSplashScreenEnabled()) {
          console.log('WaitScreenMessage: ', response.Payload.Message);
          this.fuseSplashScreenService.showWaitScreenMessage(response.Payload.Message);
        }
      }
    });

    if (window.location.hostname.includes('adminpanel')) {
      if (!Configuration.getConfiguration('IsUserAdminPanel')) {
        this.authService.logoutUser();
      }
    }

    this.webSocketAutomationService.startListening();
  }

  public resetIdleTimer(): void {
    console.log('[WEBSOCKET ACTIVITY] Resetting idle timer due to data update');
    this.sessionTimeout.resetIdleTimer();
  }

  private showCookiePolicy() {
    if (this.itpDesktopService.isDesktop()) return;
    const disableCookiePolicyPopup = this.authService.getCookiePolicyInfo();
    const key = Configuration.SharedCookiePrefix + "DisableCookiePolicyPopup";
    if (!this.cookieService.check(key) || (disableCookiePolicyPopup && disableCookiePolicyPopup == 'false')) {
      this.showCookiePolicyPopup = true;
    }
  }

  onDisableCookiePolicyPopup(): any {
    const expiryTimeThreshHold = parseInt(Configuration.cookiePolicyCookieExpiryInMilliseconds, 10);
    const expiryTime = new Date(new Date().getTime() + expiryTimeThreshHold);
    this.cookieService.set(Configuration.SharedCookiePrefix + 'DisableCookiePolicyPopup', "true", expiryTime, "/", Configuration.CookieDomainName);
    this.showCookiePolicyPopup = false;
  }

  removeParam(key, sourceURL) {
    let rtn = sourceURL.split('?')[0],
      param,
      params_arr = [],
      queryString = (sourceURL.indexOf('?') !== -1) ? sourceURL.split('?')[1] : '';
    if (queryString !== '') {
      params_arr = queryString.split('&');
      for (let i = params_arr.length - 1; i >= 0; i -= 1) {
        param = params_arr[i].split('=')[0];
        if (param === key) {
          params_arr.splice(i, 1);
        }
      }
      rtn = rtn + '?' + params_arr.join('&');
    }
    return rtn;
  }

  /** The "Session timeout" countdown is on screen (in this tab, and in every other tab of the session). */
  get isSessionTimeOutCountVisible(): boolean {
    return this.sessionTimeout.countdownVisible;
  }

  get formattedCountDown(): string {
    return this.sessionTimeout.formattedCountDown;
  }

  /** "Stay in Orbitax": closes the countdown in every tab and keeps the session. */
  onStay() {
    console.warn('[USER IDLE] Stay At Clicked');
    this.sessionTimeout.stay();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.sessionTimeout.stop();
    this.presenceTrackingService.stopTracking();
    this.missionWorkTab.stop();
    this.socketClusterService.unsubscribeChannel(Configuration.getConfig('sessionId'));
    this.webSocketAutomationService.stopListening();
  }

  private uncoverFuseSpashContents() {
    const elements = document.querySelectorAll('[data-index-hidden-element]');
    elements.forEach((el) => {
      el.classList.remove('hide');
    });
  }

  private processLogger() {
    logger.log = console.log;
    logger.info = console.info;
    logger.warn = console.warn;
    if(!Configuration.IsInfoLogEnabled){
      console.log = (...args) => {
      }
      console.info = (...args) => {
      }
    }
    if(!Configuration.IsWarningLogEnabled){
      console.warn = (...args) => {
      }
    }
  }
}
